/**
 * Hazır etiket şablonları.
 *
 * Kullanıcının boş tuvalle başlamaması için. Ölçüler piyasada yaygın etiket
 * rulolarına göre seçildi. "Raf Fiyat Etiketi (40x90)" şablonu, Argox OS-214 plus
 * ile canlı test edilip doğrulanan düzenin birebir karşılığıdır.
 */

import {
  LabelTemplate,
  createBarcodeElement,
  createLineElement,
  createQrElement,
  createTextElement,
  createRollLayout,
  fitSheetLayout,
  DEFAULT_DPI,
} from '../../types/labelTemplate.types';

export interface LabelPreset {
  id: string;
  label: string;
  description: string;
  build: () => LabelTemplate;
}

/** Raf fiyat etiketi — 40 mm rulo genişliği, 90 mm uzunluk, yatay okunur */
function buildShelfPrice(): LabelTemplate {
  return {
    name: 'Raf Fiyat Etiketi (40x90)',
    dataSource: 'inventory',
    labelWidthMm: 40,
    labelLengthMm: 90,
    rotation: 90, // dikey beslenir, yatay okunur
    dpi: DEFAULT_DPI,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: createRollLayout(),
    elements: [
      createTextElement({
        text: '{{urunAdi}}',
        xMm: 3,
        yMm: 2,
        widthMm: 80,
        heightMm: 6,
        fontSizeMm: 4,
        bold: true,
      }),
      createLineElement({ xMm: 3, yMm: 9.5, widthMm: 80, heightMm: 0.4 }),
      createTextElement({
        text: '{{fiyatKdvli}} {{paraSembol}}',
        xMm: 30,
        yMm: 12,
        widthMm: 53,
        heightMm: 14,
        fontSizeMm: 12,
        bold: true,
        align: 'right',
      }),
      createTextElement({
        text: '{{barkod}}',
        xMm: 3,
        yMm: 27,
        widthMm: 45,
        heightMm: 3.5,
        fontSizeMm: 2.6,
      }),
      createTextElement({
        text: '{{stokKodu}}',
        xMm: 3,
        yMm: 31,
        widthMm: 45,
        heightMm: 3.5,
        fontSizeMm: 2.6,
      }),
    ],
  };
}

/** Barkodlu ürün etiketi — küçük, rulo, barkod ağırlıklı */
function buildProductBarcode(): LabelTemplate {
  return {
    name: 'Ürün Barkod Etiketi (40x30)',
    dataSource: 'inventory',
    labelWidthMm: 40,
    labelLengthMm: 30,
    rotation: 0,
    dpi: DEFAULT_DPI,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: createRollLayout(),
    elements: [
      createTextElement({
        text: '{{urunAdi}}',
        xMm: 2,
        yMm: 1.5,
        widthMm: 36,
        heightMm: 7,
        fontSizeMm: 2.8,
        bold: true,
        align: 'center',
        lineHeight: 1.15,
      }),
      createBarcodeElement({
        barcodeValue: '{{barkod}}',
        barcodeFormat: 'CODE128',
        xMm: 3,
        yMm: 9,
        widthMm: 34,
        heightMm: 12,
        showBarcodeText: true,
      }),
      createTextElement({
        text: '{{fiyatKdvli}} {{paraSembol}}',
        xMm: 2,
        yMm: 23,
        widthMm: 36,
        heightMm: 5,
        fontSizeMm: 4,
        bold: true,
        align: 'center',
      }),
    ],
  };
}

/** Tartı etiketi — ağırlık, birim fiyat ve toplam bir arada */
function buildScaleLabel(): LabelTemplate {
  return {
    name: 'Tartı Etiketi (60x40)',
    dataSource: 'inventory',
    labelWidthMm: 60,
    labelLengthMm: 40,
    rotation: 0,
    dpi: DEFAULT_DPI,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: createRollLayout(),
    elements: [
      createTextElement({
        text: '{{urunAdi}}',
        xMm: 2,
        yMm: 1.5,
        widthMm: 56,
        heightMm: 5,
        fontSizeMm: 3.4,
        bold: true,
      }),
      createLineElement({ xMm: 2, yMm: 7.5, widthMm: 56, heightMm: 0.3 }),
      createTextElement({
        text: 'Ağırlık: {{agirlik}} {{birim}}',
        xMm: 2,
        yMm: 9,
        widthMm: 34,
        heightMm: 4,
        fontSizeMm: 2.8,
      }),
      createTextElement({
        text: 'Birim: {{birimFiyat}}',
        xMm: 2,
        yMm: 13.5,
        widthMm: 34,
        heightMm: 4,
        fontSizeMm: 2.8,
      }),
      createTextElement({
        text: '{{fiyatKdvli}} {{paraSembol}}',
        xMm: 2,
        yMm: 18.5,
        widthMm: 56,
        heightMm: 8,
        fontSizeMm: 7,
        bold: true,
        align: 'right',
      }),
      createBarcodeElement({
        barcodeValue: '{{barkod}}',
        barcodeFormat: 'CODE128',
        xMm: 2,
        yMm: 27.5,
        widthMm: 56,
        heightMm: 10,
        showBarcodeText: true,
      }),
    ],
  };
}

/** A4 lazer yazıcı için sayfaya dizilen küçük etiket */
function buildA4Sheet(): LabelTemplate {
  const labelW = 63.5;
  const labelH = 38.1;
  return {
    name: 'A4 Sayfa Etiketi (63.5x38.1 - 3x7)',
    dataSource: 'inventory',
    labelWidthMm: labelW,
    labelLengthMm: labelH,
    rotation: 0,
    dpi: 300,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: fitSheetLayout(labelW, labelH, { gapXMm: 2.5, gapYMm: 0, marginMm: 8 }),
    elements: [
      createTextElement({
        text: '{{urunAdi}}',
        xMm: 3,
        yMm: 2,
        widthMm: 57,
        heightMm: 8,
        fontSizeMm: 3,
        bold: true,
        lineHeight: 1.15,
      }),
      createBarcodeElement({
        barcodeValue: '{{barkod}}',
        xMm: 3,
        yMm: 11,
        widthMm: 40,
        heightMm: 11,
        showBarcodeText: true,
      }),
      createTextElement({
        text: '{{fiyatKdvli}} {{paraSembol}}',
        xMm: 44,
        yMm: 13,
        widthMm: 17,
        heightMm: 6,
        fontSizeMm: 4.2,
        bold: true,
        align: 'right',
      }),
      createTextElement({
        text: '{{stokKodu}}',
        xMm: 3,
        yMm: 23.5,
        widthMm: 57,
        heightMm: 3.5,
        fontSizeMm: 2.4,
      }),
    ],
  };
}

/** Depo lokasyon etiketi — QR + büyük lokasyon kodu */
function buildWmsLocation(): LabelTemplate {
  return {
    name: 'Depo Lokasyon Etiketi (100x50)',
    dataSource: 'wmsLocation',
    labelWidthMm: 100,
    labelLengthMm: 50,
    rotation: 0,
    dpi: DEFAULT_DPI,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: createRollLayout(),
    elements: [
      createQrElement({
        qrValue: '{{lokasyonKodu}}',
        xMm: 3,
        yMm: 3,
        widthMm: 28,
        heightMm: 28,
      }),
      createTextElement({
        text: '{{lokasyonKodu}}',
        xMm: 34,
        yMm: 4,
        widthMm: 63,
        heightMm: 12,
        fontSizeMm: 9,
        bold: true,
      }),
      createTextElement({
        text: 'Peron {{peron}} / Raf {{raf}}',
        xMm: 34,
        yMm: 18,
        widthMm: 63,
        heightMm: 6,
        fontSizeMm: 4,
      }),
      createTextElement({
        text: '{{depoAdi}}',
        xMm: 34,
        yMm: 25,
        widthMm: 63,
        heightMm: 5,
        fontSizeMm: 3,
      }),
      createBarcodeElement({
        barcodeValue: '{{lokasyonKodu}}',
        xMm: 3,
        yMm: 33,
        widthMm: 94,
        heightMm: 13,
        showBarcodeText: true,
      }),
    ],
  };
}

/** Boş şablon — sıfırdan tasarlamak için */
function buildBlank(): LabelTemplate {
  return {
    name: 'Yeni Şablon',
    dataSource: 'inventory',
    labelWidthMm: 40,
    labelLengthMm: 30,
    rotation: 0,
    dpi: DEFAULT_DPI,
    printerLanguage: 'BROWSER',
    backgroundColor: '#ffffff',
    layout: createRollLayout(),
    elements: [],
  };
}

export const LABEL_PRESETS: LabelPreset[] = [
  {
    id: 'shelf-price',
    label: 'Raf Fiyat Etiketi',
    description: '40 × 90 mm, dikey beslenir yatay okunur. Ürün adı, büyük fiyat, barkod no.',
    build: buildShelfPrice,
  },
  {
    id: 'product-barcode',
    label: 'Ürün Barkod Etiketi',
    description: '40 × 30 mm rulo. Ürün adı, CODE128 barkod, fiyat.',
    build: buildProductBarcode,
  },
  {
    id: 'scale',
    label: 'Tartı Etiketi',
    description: '60 × 40 mm. Ağırlık, birim fiyat, toplam tutar ve barkod.',
    build: buildScaleLabel,
  },
  {
    id: 'a4-sheet',
    label: 'A4 Sayfa Etiketi',
    description: '63.5 × 38.1 mm, A4 sayfaya 3 × 7 = 21 etiket. Normal lazer yazıcı için.',
    build: buildA4Sheet,
  },
  {
    id: 'wms-location',
    label: 'Depo Lokasyon Etiketi',
    description: '100 × 50 mm. QR kod, büyük lokasyon kodu, peron/raf bilgisi.',
    build: buildWmsLocation,
  },
  {
    id: 'blank',
    label: 'Boş Şablon',
    description: 'Sıfırdan tasarla.',
    build: buildBlank,
  },
];

export function buildPreset(id: string): LabelTemplate {
  const preset = LABEL_PRESETS.find((p) => p.id === id);
  return (preset ?? LABEL_PRESETS[LABEL_PRESETS.length - 1]).build();
}
